import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';

export default function AboutPage() {
  const [contactStatus, setContactStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [contactError, setContactError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="mobile_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setContactStatus('success');
      return;
    }

    try {
      const formData = new FormData(form);
      const resp = await fetch('https://bodymindapp.github.io/api/form/d9u4k75ca23r0tr1aqf0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await resp.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (resp.ok && parsed?.code === 'OK') {
        setContactStatus('success');
        form.reset();
      } else {
        setContactStatus('error');
        setContactError(parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试');
      }
    } catch {
      setContactStatus('error');
      setContactError('网络错误，请稍后重试');
    }
  };

  const siteUrl = 'https://bodymindapp.github.io';

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 — 手淫的科学认知与健康指南',
    description: '我们致力于提供基于医学与心理学的权威性健康科普内容，帮助成年人科学认识手淫行为。',
    url: `${siteUrl}/about`,
  };

  return (
    <>
      <SEOHead
        title="关于我们 | 手淫的科学认知与健康指南"
        description="了解健康认知指南的品牌故事与使命。我们致力于提供基于医学与心理学的权威性健康科普，帮助成年人建立理性的自我认知。联系我们的专业团队获取更多信息。"
        keywords="关于我们,性健康教育,科普平台,联系我们"
        canonicalUrl={`${siteUrl}/about`}
        schemaJson={aboutSchema}
      />
      <Navbar />
      <main className="pt-14">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="py-12 md:py-16 px-4 md:px-6 bg-white">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-primary-100 flex items-center justify-center">
              <i className="ri-mental-health-line text-3xl text-primary-600"></i>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
              关于健康认知指南
            </h1>
            <p className="text-foreground-500 leading-relaxed max-w-2xl mx-auto">
              我们是一个致力于性健康科普的独立平台，由医学和心理学背景的专业团队创建和维护。
              我们的使命是为每一位成年人提供<strong className="text-foreground-800">科学、理性、无偏见</strong>的性健康知识。
            </p>
          </div>
        </section>

        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-focus-2-line text-2xl text-primary-600"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2">科学为本</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  所有内容基于循证医学和心理学研究，参考世界卫生组织等权威机构的指导方针。
                </p>
              </div>
              <div className="text-center p-6">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-shield-check-line text-2xl text-accent-700"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2">去污名化</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  坚持无偏见、无歧视的科普态度，帮助读者摆脱文化羞耻感的束缚。
                </p>
              </div>
              <div className="text-center p-6">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-secondary-100 flex items-center justify-center">
                  <i className="ri-user-heart-line text-2xl text-secondary-700"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2">以人为本</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  关注每一位读者的真实需求，提供有温度、易理解、可操作的健康知识。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 px-4 md:px-6 bg-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6 text-center">
              我们的团队
            </h2>
            <p className="text-foreground-500 leading-relaxed mb-8 text-center">
              我们的内容团队由具有医学背景的撰稿人和审校专家组成，包括临床医学、心理学和公共卫生领域的专业人士。
              每篇文章在发布前都经过严格的<strong className="text-foreground-800">同行评审</strong>流程，确保信息的准确性和时效性。
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="p-5 rounded-lg bg-background-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-3-line text-primary-600"></i>
                  </div>
                  <div>
                    <p className="font-medium text-foreground-800 text-sm">李明</p>
                    <p className="text-xs text-foreground-500">医学顾问 / 临床医学博士</p>
                  </div>
                </div>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  三甲医院泌尿外科主治医师，专注男性健康和性医学研究十年。
                </p>
              </div>
              <div className="p-5 rounded-lg bg-background-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-3-line text-accent-700"></i>
                  </div>
                  <div>
                    <p className="font-medium text-foreground-800 text-sm">林悦</p>
                    <p className="text-xs text-foreground-500">心理学编辑 / 应用心理学硕士</p>
                  </div>
                </div>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  国家二级心理咨询师，认知行为治疗方向，专注性心理学和伴侣关系咨询。
                </p>
              </div>
              <div className="p-5 rounded-lg bg-background-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-3-line text-secondary-700"></i>
                  </div>
                  <div>
                    <p className="font-medium text-foreground-800 text-sm">王芳</p>
                    <p className="text-xs text-foreground-500">主编 / 公共卫生硕士</p>
                  </div>
                </div>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  曾任健康类媒体资深编辑，致力于将专业的医学知识转化为公众可理解的内容。
                </p>
              </div>
              <div className="p-5 rounded-lg bg-background-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-3-line text-primary-600"></i>
                  </div>
                  <div>
                    <p className="font-medium text-foreground-800 text-sm">刘洋</p>
                    <p className="text-xs text-foreground-500">研究员 / 流行病学博士</p>
                  </div>
                </div>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  大学公共卫生学院研究员，专注行为流行病学和性健康促进研究。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6 text-center">
              联系我们
            </h2>
            <p className="text-foreground-500 text-center mb-8 text-sm">
              如有任何问题、建议或合作意向，欢迎通过以下表单与我们联系。我们通常会在2个工作日内回复。
            </p>

            {contactStatus === 'success' ? (
              <div className="bg-primary-50 text-primary-700 px-5 py-4 rounded-lg text-center text-sm">
                感谢您的留言！我们会尽快回复。
              </div>
            ) : (
              <form
                onSubmit={handleContactSubmit}
                data-readdy-form
                className="bg-white rounded-lg p-6 space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1">
                      姓名 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all duration-200"
                      placeholder="您的姓名"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1">
                      邮箱 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all duration-200"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1">
                    主题
                  </label>
                  <input
                    type="text"
                    name="subject"
                    className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all duration-200"
                    placeholder="请输入主题"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1">
                    留言 <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all duration-200 resize-none"
                    placeholder="请输入您的留言（最多500字）"
                  ></textarea>
                </div>
                <input
                  type="text"
                  name="mobile_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                  aria-hidden="true"
                  className="form-website-alt-field"
                />
                <button
                  type="submit"
                  className="w-full py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm"
                >
                  提交留言
                </button>
              </form>
            )}
            {contactStatus === 'error' && (
              <p className="text-red-500 text-sm text-center mt-3">{contactError}</p>
            )}

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="text-center p-4">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-mail-line text-primary-600"></i>
                </div>
                <p className="text-xs text-foreground-500">contact@health-sex-edu.com</p>
              </div>
              <div className="text-center p-4">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-map-pin-line text-accent-700"></i>
                </div>
                <p className="text-xs text-foreground-500">中国 · 北京</p>
              </div>
              <div className="text-center p-4">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-secondary-100 flex items-center justify-center">
                  <i className="ri-time-line text-secondary-700"></i>
                </div>
                <p className="text-xs text-foreground-500">周一至周五 9:00-18:00</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 px-4 md:px-6 bg-white">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
              免责声明
            </h2>
            <p className="text-sm text-foreground-500 leading-relaxed">
              本站所有内容仅供健康科普与教育参考，不构成医疗诊断、治疗建议或处方。如果您有具体的健康问题，
              请务必咨询有资质的医疗专业人员。我们努力确保内容的准确性，但不对信息的完整性或时效性做任何保证。
              使用本站内容即表示您理解并同意此免责声明。
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}